import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { UsersThree, Barbell, Pulse, Fire, Gauge, Warning, X, ArrowRight, Scales, Question } from "@phosphor-icons/react";
import AppHealthCard from "../components/AppHealthCard";
import FeatureTour from "../components/FeatureTour";

function StatCard({ label, value, icon: Icon, color = "#FF3B30" }) {
  return (
    <div className="card-base card-lift stagger-item p-6" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-widest text-muted">{label}</span>
        <Icon size={22} weight="bold" style={{ color }} />
      </div>
      <div className="font-display font-black text-5xl tracking-tighter" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

const FATIGUE_CONFIG = {
  critical: { color: "#FF3B30", label: "Crítico" },
  high: { color: "#FF9500", label: "Alto" },
  moderate: { color: "#FFD60A", label: "Moderado" },
  optimal: { color: "#32D74B", label: "Óptimo" },
  no_data: { color: "#A3A3A3", label: "Sin datos" },
};

const IMC_CONFIG = {
  underweight: { color: "#FFD60A", label: "Bajo peso", short: "BAJO" },
  normal: { color: "#32D74B", label: "Normal", short: "NORMAL" },
  overweight: { color: "#FF9500", label: "Sobrepeso", short: "SOBREP" },
  obese_1: { color: "#FF3B30", label: "Obesidad 1", short: "OB 1" },
  obese_2: { color: "#FF3B30", label: "Obesidad 2", short: "OB 2" },
  obese_3: { color: "#8B0000", label: "Obesidad 3", short: "OB 3" },
};

const ICC_CONFIG = {
  normal: { color: "#32D74B", label: "Normal" },
  risk: { color: "#FF3B30", label: "Riesgo" },
};

function NutritionBadge({ imc, imcLevel, icc, iccLevel }) {
  if (!imc && !icc) {
    return <span className="text-[10px] text-muted uppercase tracking-widest">Sin datos</span>;
  }
  const imcCfg = imcLevel ? IMC_CONFIG[imcLevel] : null;
  const iccCfg = iccLevel ? ICC_CONFIG[iccLevel] : null;
  return (
    <div className="flex flex-col gap-1 items-end">
      {imcCfg && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          style={{ backgroundColor: imcCfg.color, color: imcLevel === "underweight" ? "#000" : "#fff" }}
          title={`IMC ${imc} · ${imcCfg.label}`}
        >
          <span>IMC {imc}</span>
          <span className="opacity-80">·</span>
          <span>{imcCfg.short}</span>
        </span>
      )}
      {iccCfg && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border"
          style={{ borderColor: iccCfg.color, color: iccCfg.color }}
          title={`ICC ${icc} · ${iccCfg.label}`}
        >
          ICC {icc} · {iccCfg.label}
        </span>
      )}
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [exerciseCount, setExerciseCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [ov, al, ex] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/alerts", { params: { only_unread: true } }),
        api.get("/exercises"),
      ]);
      setOverview(ov.data);
      setAlerts(al.data);
      setExerciseCount(ex.data.length);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dismissAlert = async (id) => {
    try {
      await api.put(`/admin/alerts/${id}/read`);
      setAlerts((a) => a.filter((x) => x.alert_id !== id));
    } catch {}
  };

  const totalUsers = overview.length;
  const criticalCount = overview.filter((u) => u.fatigue_level === "critical").length;
  const highCount = overview.filter((u) => u.fatigue_level === "high").length;
  const nutritionAlertCount = overview.filter((u) => ["overweight", "obese_1", "obese_2", "obese_3", "underweight"].includes(u.imc_level) || u.icc_level === "risk").length;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const days = Math.floor((Date.now() - d.getTime()) / (86400000));
      if (days === 0) return "Hoy";
      if (days === 1) return "Ayer";
      if (days < 30) return `Hace ${days} días`;
      return d.toLocaleDateString("es-AR");
    } catch {
      return iso;
    }
  };

  return (
    <>
      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="alerts-banner">
          {alerts.map((a) => (
            <div key={a.alert_id} className="card-base border-l-4 !border-l-[#FF3B30] p-4 flex items-center justify-between gap-4" data-testid={`alert-${a.alert_id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Warning size={22} weight="duotone" className="text-[#FF3B30] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-display font-bold uppercase text-sm tracking-tight">
                    ⚠ {a.user_name} entró en fatiga crítica
                  </div>
                  <div className="text-xs text-muted truncate">
                    Índice: <span className="text-white font-semibold">{a.fatigue_score}/100</span> ·
                    RPE 7d: <span className="text-white font-semibold">{a.avg_rpe_7d ?? "—"}</span> ·
                    Wellness: <span className="text-white font-semibold">{a.wellness_score ?? "—"}/25</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => navigate(`/profile/${a.user_id}`)} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1" data-testid={`alert-open-${a.alert_id}`}>
                  Ver perfil <ArrowRight size={12} weight="bold" />
                </button>
                <button onClick={() => dismissAlert(a.alert_id)} className="btn-secondary !py-2 !px-3" title="Descartar" data-testid={`alert-dismiss-${a.alert_id}`}>
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6 stagger-in">
        <StatCard label="Usuarios" value={totalUsers} icon={UsersThree} color="#FF3B30" />
        <StatCard label="Ejercicios" value={exerciseCount} icon={Barbell} color="#007AFF" />
        <StatCard label="En Alerta" value={criticalCount} icon={Warning} color="#FF3B30" />
        <StatCard label="Riesgo Alto" value={highCount} icon={Gauge} color="#FF9500" />
        <StatCard label="Alertas Nutric." value={nutritionAlertCount} icon={Scales} color="#FFD60A" />
      </div>

      <div className="mb-10">
        <AppHealthCard />
      </div>

      <div className="card-base p-6" data-testid="athletes-overview">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="font-display font-black uppercase text-2xl tracking-tight">Atletas · Ranking por Fatiga</h2>
            <p className="text-xs text-muted mt-1">Ordenado de mayor a menor riesgo</p>
          </div>
        </div>

        {loading ? (
          <div className="text-muted py-8 text-center">Cargando...</div>
        ) : overview.length === 0 ? (
          <div className="text-muted py-8 text-center">No hay atletas cargados todavía.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-app">
                  <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Atleta</th>
                  <th className="py-3 px-2 text-left text-[10px] uppercase tracking-widest text-muted">Estado</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted">Índice</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted">RPE 7d</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted hidden md:table-cell">Δ Vol</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted hidden md:table-cell">Wellness</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted hidden lg:table-cell">Nutrición</th>
                  <th className="py-3 px-2 text-right text-[10px] uppercase tracking-widest text-muted hidden lg:table-cell">Última sesión</th>
                  <th className="py-3 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {overview.map((u) => {
                  const cfg = FATIGUE_CONFIG[u.fatigue_level] || FATIGUE_CONFIG.no_data;
                  return (
                    <tr key={u.user_id} className="border-b border-app hover:bg-surface-hover transition-colors" data-testid={`athlete-row-${u.user_id}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center font-display font-black text-xs overflow-hidden flex-shrink-0">
                            {u.avatar_url ? (
                              <img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" />
                            ) : (
                              u.name?.[0]?.toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="font-semibold truncate">{u.name}</div>
                            {u.sport && <div className="text-[10px] uppercase tracking-widest text-muted">{u.sport}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest"
                          style={{ backgroundColor: cfg.color, color: u.fatigue_level === "moderate" || u.fatigue_level === "no_data" ? "#000" : "#fff" }}
                        >
                          {cfg.label}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right font-display font-black text-xl" style={{ color: cfg.color }}>
                        {u.fatigue_score != null ? Math.round(u.fatigue_score) : "—"}
                      </td>
                      <td className="py-3 px-2 text-right font-semibold">{u.avg_rpe_7d ?? "—"}</td>
                      <td className="py-3 px-2 text-right hidden md:table-cell font-semibold" style={{ color: u.volume_change_pct > 30 ? "#FF9500" : undefined }}>
                        {u.volume_change_pct != null ? `${u.volume_change_pct >= 0 ? "+" : ""}${u.volume_change_pct}%` : "—"}
                      </td>
                      <td className="py-3 px-2 text-right hidden md:table-cell font-semibold">
                        {u.wellness_score != null ? `${u.wellness_score}/25` : "—"}
                      </td>
                      <td className="py-3 px-2 text-right hidden lg:table-cell">
                        <NutritionBadge imc={u.imc} imcLevel={u.imc_level} icc={u.icc} iccLevel={u.icc_level} />
                      </td>
                      <td className="py-3 px-2 text-right hidden lg:table-cell text-muted text-xs">{fmtDate(u.last_session_at)}</td>
                      <td className="py-3 px-2 text-right">
                        <button onClick={() => navigate(`/profile/${u.user_id}`)} className="btn-secondary !py-1.5 !px-2 text-xs flex items-center gap-1" data-testid={`open-athlete-${u.user_id}`}>
                          <ArrowRight size={12} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function UserDashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tests: 0, routines: 0 });
  useEffect(() => {
    (async () => {
      try {
        const [t, r] = await Promise.all([
          api.get(`/users/${user.user_id}/tests`),
          api.get(`/users/${user.user_id}/routines`),
        ]);
        setStats({ tests: t.data.length, routines: r.data.length });
      } catch {}
    })();
  }, [user]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 mb-10 stagger-in">
        <StatCard label="Testeos" value={stats.tests} icon={Pulse} color="#FF3B30" />
        <StatCard label="Rutinas" value={stats.routines} icon={Fire} color="#FFD60A" />
      </div>
      <div className="card-base p-8">
        <h2 className="font-display uppercase font-black text-2xl tracking-tight mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={() => navigate(`/profile/${user.user_id}`)} className="btn-secondary" data-testid="qa-profile">Mi Perfil</button>
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [showTour, setShowTour] = React.useState(false);

  React.useEffect(() => {
    if (!user || user.role === "admin") return;
    // Mostrar tour si el atleta ya hizo onboarding pero todavía no vio el tour.
    if (user.onboarded && !user.profile?.tour_completed_at) {
      setShowTour(true);
    }
  }, [user]);

  const handleTourComplete = () => {
    setShowTour(false);
    if (user) {
      setUser({
        ...user,
        profile: { ...(user.profile || {}), tour_completed_at: new Date().toISOString() },
      });
    }
  };

  return (
    <Layout>
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-muted mb-2 flex items-center gap-3">
          <span>Panel Principal</span>
          {user?.role !== "admin" && (
            <button
              onClick={() => setShowTour(true)}
              className="text-muted hover:text-primary-app text-[10px] uppercase tracking-widest flex items-center gap-1"
              data-testid="open-tour-btn"
              title="Ver el tutorial nuevamente"
            >
              <Question size={12} weight="bold" /> Ver tutorial
            </button>
          )}
        </div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter uppercase">
          Hola, <span className="text-primary-app">{user?.name?.split(" ")[0]}</span>
        </h1>
        <p className="text-muted mt-2">
          {user?.role === "admin"
            ? "Monitoreá el estado de todos los atletas de un vistazo."
            : "Consultá tu perfil, rutinas, testeos y tu plan nutricional."}
        </p>
      </div>

      {user?.role === "admin" ? <AdminDashboard /> : <UserDashboard user={user} />}

      {showTour && user && <FeatureTour user={user} onComplete={handleTourComplete} />}
    </Layout>
  );
}
